<div id="head"></div>
<h1 class="sr-only">CSS Gradient Generator - Build Linear, Radial &amp; Conic Gradients</h1>

<!-- ===================== Hero: the gradient builder; backdrop = the live gradient ===================== -->
<div class="default-section initial-section metallic-section no-buttons" data-section="top"
     [class.cp-hero-noblur]="!!uploadedImg"
     [style.--hero-bg]="heroBgValue">
  <!-- the gradient's own title and its source as plain text on top, as on the palette tool (owner 2026-10-12) -->
  <div class="cp-hero-head" [ngSwitch]="subtitle.kind">
    <ng-container *ngSwitchCase="'theme'">
      <p class="tools-title" aria-hidden="true">Codigrate Theme Gradient</p>
      <p class="tools-subtitle">Source: {{ subtitle.themeName }}</p>
    </ng-container>
    <ng-container *ngSwitchCase="'image'">
      <p class="tools-title" aria-hidden="true">Image Gradient</p>
      <p class="tools-subtitle">Source: {{ subtitle.imageName || 'uploaded image' }}</p>
    </ng-container>
    <ng-container *ngSwitchCase="'harmony'">
      <p class="tools-title" aria-hidden="true">{{ schemeTitle }} Gradient</p>
      <p class="tools-subtitle">Source: {{ subtitle.name }}</p>
    </ng-container>
    <ng-container *ngSwitchCase="'full'">
      <p class="tools-title" aria-hidden="true">Random Gradient</p>
      <p class="tools-subtitle">Source: {{ subtitle.seed ? 'seed-' + subtitle.seed : 'Random' }}</p>
    </ng-container>
    <ng-container *ngSwitchCase="'named'">
      <p class="tools-title" aria-hidden="true">Featured Gradient</p>
      <p class="tools-subtitle">Source: {{ subtitle.name }}</p>
    </ng-container>
    <ng-container *ngSwitchDefault>
      <p class="tools-title" aria-hidden="true">Custom Theme</p>
      <p class="tools-subtitle">Source: {{ subtitle.seed ? 'seed-' + subtitle.seed : 'Custom' }}</p>
    </ng-container>
  </div>
  <app-gradient-widget
    [palette]="palette"
    [subtitle]="subtitle"
    [scheme]="currentGenerator"
    [type]="type"
    [angle]="angle"
    [count]="count"
    (countChange)="count = $event"
    [positions]="positions"
    (positionChange)="onPositionChange( $event )"
    (editColor)="onEditColor( $event )"
    (removeColor)="removeColor( $event )"
    (reverse)="reverseStops()"
    (generate)="onGenerate( $event )"
    (uploadImage)="handleImageUpload( $event )"
    (typeChange)="onTypeChange( $event )"
    (angleChange)="onAngleChange( $event )"
    (insertStop)="insertStop( $event )">
  </app-gradient-widget>
</div>

<!-- a chip per section under the hero, one line that slides sideways and stays under the nav (owner 2026-10-12) -->
<nav class="cp-chipbar" appStuck aria-label="Gradient tool sections">
  <div class="cp-chipbar-row">
    <a *ngFor="let s of pageSections" class="pl-chip" [class.on]="activeSection === s.key" [attr.href]="'#sec-' + s.key"
       (click)="$event.preventDefault(); scrollToSection( s.key )">
      <span>{{ s.label }}</span>
    </a>
  </div>
</nav>

<!-- ===================== Body: every section as a row of two cards (owner 2026-10-12) ===================== -->
<div class="cp-body cp-boxes">

  <div class="cp-content" [style.--gr-lf]="analysisLabelFont + 'px'">

    <!-- Preview: the rendered gradient on the left, its CSS (syntax-highlighted to
         the active theme, copyable) on the right - same window layout as the
         palette tool's preview. -->
    <section class="cp-section" id="sec-preview" data-section="preview">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="gradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Preview</h2>
      <p class="cp-section-lead">Your palette spread along the gradient, with the CSS ready to copy</p>
      </header>
      <div class="cp-main">

      <div class="cp-pv">
        <div class="gr-preview-card" role="img" [style.background]="gradient()" aria-label="Gradient preview"></div>

        <div class="cp-window cp-pv-code">
          <div class="cp-window-bar">
            <span class="cp-window-dots"><i></i><i></i><i></i></span>
            <span class="cp-window-file">gradient.css</span>
            <button type="button" class="cp-pv-copy cp-std-btn cp-std-btn-copy" (click)="copyCss()">
              <app-icon name="copy" aria-hidden="true"></app-icon>{{ copied ? 'Copied' : 'Copy' }}
            </button>
          </div>
          <pre class="cp-window-code"
               [style.--cp-editor-bg]="editorBg || null"
               [style.--cp-editor-fg]="editorFg || null"
               [style.--cp-syntax-comment]="synComment || null"
               [style.--cp-syntax-selector]="synSelector || null"
               [style.--cp-syntax-property]="synProperty || null"
               [style.--cp-syntax-value]="synValue || null"
               [style.--cp-syntax-keyword]="synKeyword || null"
               [style.--cp-syntax-punctuation]="synPunctuation || null"><code class="cp-pv-codeblock"><span class="cp-css-rule" [innerHTML]="gradientCssHtml"></span></code></pre>
        </div>
      </div>
      </div>
    </section>

    <!-- Color Channels: the palette's overall color balance (aggregate). -->
    <section class="cp-section" id="sec-channels" data-section="channels" *ngIf="channelStats">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="gradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Color Channels</h2>
      <p class="cp-section-lead">Averaged across the whole palette. RGB is each channel's share of the total, plus the average HSL, HSV and CMYK</p>
      </header>
      <div class="cp-main">

      <div class="cp-channel-groups">
        <div class="cp-channel-group">
          <h3 class="cp-h3">RGB</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.rgb">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">CMYK</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.cmyk">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">HSL</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.hsl">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <div class="cp-channel-group">
          <h3 class="cp-h3">HSV</h3>
          <div class="cp-channels">
            <div class="cp-channel" *ngFor="let c of channelStats.hsv">
              <span class="cp-channel-label">{{ c.label }}</span>
              <span class="cp-channel-track">
                <span class="cp-channel-fill" [style.width.%]="c.width" [style.background]="c.color"></span>
              </span>
              <span class="cp-channel-val">{{ c.text }}</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- Harmony -->
    <section class="cp-section" id="sec-harmony" data-section="harmony" *ngIf="harmonyResult.length > 0">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="gradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Harmony</h2>
      <p class="cp-section-lead">A harmonic score per scheme: the share of your palette whose colors form that relationship on the wheel, shown as a gradient of the colors that take part</p>
      </header>
      <div class="cp-main">

      <div class="cp-scheme" *ngFor="let h of harmonyScores; index as hi">
        <h3 class="cp-h3 cp-harmony-head" [class.cp-h3-gap]="hi > 0" [class.cp-harmony-zero]="h.score === 0">
          <span class="cp-harmony-name">{{ h.name }}:</span>
          <span class="cp-harmony-dot" [attr.data-level]="harmonyLevel( h.score, palette.length )"></span>
          <span class="cp-harmony-score">{{ h.score }}</span>
        </h3>
        <p class="cp-scheme-desc">{{ h.description }}</p>
        <div role="img" class="gr-harmony-bar"
             [style.background]="harmonyBar( h.counts )" [attr.aria-label]="h.name + ' gradient'"></div>
        <div class="gr-harmony-labels">
          <span class="gr-harmony-label" *ngFor="let c of palette; index as i"
                [class.gr-harmony-label-muted]="h.counts[i] <= 0"
                [style.left.%]="harmonyStopLeft( i )">{{ h.counts[i] }}</span>
        </div>
      </div>

      <div class="cp-scheme">
        <h3 class="cp-h3 cp-harmony-head cp-h3-gap">
          <span class="cp-harmony-name">Temperature:</span>
          <span class="cp-harmony-dot" data-level="high"></span>
          <span class="cp-harmony-score">{{ harmonyResult[ 3 ].decision | titlecase }}</span>
        </h3>
        <p class="cp-scheme-desc">Warm hues against cool ones. Their balance sets the overall mood.</p>
        <div role="img" class="gr-harmony-bar" [style.background]="temperatureBar()" aria-label="Temperature gradient"></div>
        <div class="gr-harmony-labels">
          <span class="gr-harmony-label" *ngFor="let c of palette; index as i"
                [class.gr-harmony-label-muted]="harmonyResult[3].decision !== 'equal' && temperatures[i] !== harmonyResult[3].decision"
                [style.left.%]="harmonyStopLeft( i )">{{ temperatures[ i ] }}</span>
        </div>
      </div>
      </div>
    </section>

    <!-- Tints, Shades & Tones -->
    <section class="cp-section" id="sec-shades" data-section="shades" *ngIf="shadeTintTone.length">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="gradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Tints, Shades &amp; Tones</h2>
      <p class="cp-section-lead">A shade adds black, a tint adds white, a tone adds gray. Each card is your gradient one notch further; pick one and the generator takes its colors</p>
      </header>
      <div class="cp-main">

      <div class="cp-cv-group" *ngFor="let r of shadeTintTone">
        <h3 class="cp-h3">{{ r.title }}</h3>
        <div class="wp-cv-grid">
          <button type="button" class="wp-cv-item wp-cv-pick" *ngFor="let row of r.rows" (click)="usePalette( row )"
                  [attr.aria-label]="'Use this ' + r.title + ' palette'">
            <span class="wp-cv-frame" [style.background]="gradientOf( row )"></span>
            <span class="wp-cv-cap"><span class="wp-cv-dots"><i *ngFor="let c of row" [style.background]="c"></i></span></span>
          </button>
        </div>
      </div>
      </div>
    </section>

    <!-- Temperature -->
    <section class="cp-section" id="sec-temperature" data-section="temperature" *ngIf="temperatureVariants.length">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="gradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Temperature</h2>
      <p class="cp-section-lead">Your gradient along the warm to cool axis, a little further each card; pick one and the generator takes its colors</p>
      </header>
      <div class="cp-main">

      <div class="cp-cv-group" *ngFor="let r of temperatureVariants">
        <h3 class="cp-h3">{{ r.title }}</h3>
        <div class="wp-cv-grid">
          <button type="button" class="wp-cv-item wp-cv-pick" *ngFor="let row of r.rows" (click)="usePalette( row )"
                  [attr.aria-label]="'Use this ' + r.title + ' palette'">
            <span class="wp-cv-frame" [style.background]="gradientOf( row )"></span>
            <span class="wp-cv-cap"><span class="wp-cv-dots"><i *ngFor="let c of row" [style.background]="c"></i></span></span>
          </button>
        </div>
      </div>
      </div>
    </section>

    <!-- Color Vision Deficiency -->
    <section class="cp-section" id="sec-vision" data-section="vision" *ngIf="palette.length > 0">
      <header class="cp-head">
      <span class="cp-eyebrow"><span class="cp-eyebrow-swatch" [style.background]="gradient()"></span>{{ sourceLabel }}</span>
      <h2 class="cp-h2">Color Vision Deficiency</h2>
      <p class="cp-section-lead">How this gradient reads to people with color vision deficiencies. The original first, then the same gradient through each condition</p>
      </header>
      <div class="cp-main">

      <div class="wp-cv-original">
        <p class="cp-cv-cap"><span class="cp-cv-name">Original</span></p>
        <div class="wp-cv-frame" role="img" aria-label="Original gradient" [style.background]="gradient()"></div>
      </div>

      <div class="cp-cv-group" *ngFor="let group of colorVisionGroups()">
        <h3 class="cp-h3">{{ group.group }}</h3>
        <div class="wp-cv-grid">
          <div class="wp-cv-item" *ngFor="let item of group.items">
            <div class="wp-cv-frame" role="img" [attr.aria-label]="item.name + ' simulation'" [style.background]="gradientOf( item.colors )"></div>
            <p class="cp-cv-cap"><span class="cp-cv-name">{{ item.name }}:</span> <span class="cp-cv-desc">{{ item.note }}</span></p>
          </div>
        </div>
      </div>
      </div>
    </section>

    <!-- More Color Tools (owner 2026-10-22): the Color Picker, above Related Themes -->
    <app-more-tools sectionKey="more" title="More Color Tools" lead="Pick any color on your screen and read it the same way, right from your menu bar"
                    [app]="colorPicker" [source]="moreToolsSource"></app-more-tools>

    <!-- Related Themes -->
    <section class="cp-section" id="sec-themes" data-section="themes" *ngIf="relatedThemeList.length">
      <header class="cp-head">
      <h2 class="cp-h2">Related Themes</h2>
      <p class="cp-section-lead">IDE themes whose palette sits closest to this one</p>
      </header>
      <div class="cp-main">

      <div class="cp-theme-grid">
        <app-theme-card *ngFor="let t of relatedThemeList"
                        [product]="t.product"
                        [category]="t.category"
                        [compact]="false"
                        [tile]="false"
                        [selectable]="false"
                        [clickable]="true"
                        [screenshotMode]="false">
        </app-theme-card>
      </div>
      </div>
    </section>

    <!-- From The Blog -->
    <section class="cp-section" id="sec-blog" data-section="blog">
      <header class="cp-head">
      <h2 class="cp-h2">From The Blog</h2>
      <p class="cp-section-lead">Practical reads on how colors are built and combined</p>
      </header>
      <div class="cp-main">

      <div class="cp-blog-cards">
        <a class="cp-blog-card" *ngFor="let post of blogCards" [routerLink]="post.url"
           [style.--card-accent]="post.coverAccent || null">
          <img class="cp-blog-bg" [src]="post.cover" [alt]="post.title" loading="lazy" decoding="async">
          <span class="cp-blog-scrim"></span>
          <svg class="cp-blog-part" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 210 297" aria-hidden="true">
            <path d="M 31.496131,83.498219 C 17.281586,96.049685 13.920954,122.9803 24.61827,138.63631 v 0 c 8.67477,16.8641 33.95747,26.73174 51.762024,20.2132 v 0 l 54.361716,54.36558 -83.532627,83.53647 60.446297,8e-5 84.02015,-83.53655 -84.82866,-84.83262 C 113.3695,110.57782 103.49808,85.295107 86.634028,76.620298 v 0 C 80.390777,72.357466 72.362085,70.323536 64.161412,70.323536 v 0 c -12.368001,0 -25.118271,4.626039 -32.665281,13.174683"/>
          </svg>
          <span class="cp-blog-content">
            <span class="cp-blog-cat">{{ post.categoryLabel }}</span>
            <span class="cp-blog-title">{{ post.title }}</span>
            <span class="cp-blog-meta">{{ post.dateLabel }} &middot; {{ post.readingTimeMinutes }} min read</span>
          </span>
        </a>
      </div>
      </div>
    </section>


    <!-- How It Works + FAQ closes the page, after the blog: the same closer every tool has -->
    <app-tool-about [lead]="about.lead" [paragraphs]="about.paragraphs" [faq]="about.faq" ratingKey="gradient" ratingName="the Gradient Generator"></app-tool-about>

  </div>
</div>
